import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { ApiError } from '@immoaktas/api-client';
import { setFormatLocale } from '@immoaktas/shared';
import { apiErrorText, detectLanguage, i18nOptions, LANGUAGES, LOCALE, type Language } from '@immoaktas/i18n';

// All texts come from @immoaktas/i18n (E16): namespaces common + errors + web. German is the reference language.
const STORAGE_KEY = 'ia.lang';

function savedLanguage(): Language | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v && (LANGUAGES as readonly string[]).includes(v) ? (v as Language) : null;
  } catch {
    return null;
  }
}

/** Keeps number/date formatting and <html lang> in sync with the UI language. */
function applyLanguage(lng: string) {
  const l = ((LANGUAGES as readonly string[]).includes(lng) ? lng : 'de') as Language;
  setFormatLocale(LOCALE[l]);
  document.documentElement.lang = l;
}

const initial = savedLanguage() ?? detectLanguage(typeof navigator === 'undefined' ? [] : navigator.languages?.length ? navigator.languages : [navigator.language]);
i18n.on('languageChanged', applyLanguage);
void i18n.use(initReactI18next).init(i18nOptions(initial, 'web'));
applyLanguage(initial);

/** Switches the UI language and remembers the choice on this device. */
export function setLanguage(lng: Language) {
  try {
    localStorage.setItem(STORAGE_KEY, lng);
  } catch {
    /* storage blocked: language applies for this session only */
  }
  void i18n.changeLanguage(lng);
}

/** An error whose message is already translated and may be shown as is. */
export class UserError extends Error {}

/** User-facing text for any error: API errors by key/code in the UI language (never the server sentence, B8), own errors as is. */
export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.code === 'NETWORK' ? i18n.t('errors:network') : apiErrorText((k, o) => i18n.t(k, o) as string, e);
  if (e instanceof UserError) return e.message;
  return i18n.t('errors:generic');
}

export default i18n;
